/* eslint-disable react/no-array-index-key */
import { RectangleFileItemFallback, SquareFileItemFallback } from '@picsart/cloud-storage/FileItem';
import DriveProvider, { type AuthProps, FetchOptions } from '@picsart/cloud-storage/DriveContext';
import { IFilesPageParams } from '@picsart/cloud-storage/models';
import AnalyticsService, { AnalyticsEnum } from '@picsart/shared-services/services/analytics';
import { DriveHeaderFallback } from '@picsart/cloud-storage/DriveHeader';
import { SortOrder, SortType } from '@picsart/cloud-storage/configs';
import PortalTracker from '@picsart/cloud-storage/PortalTracker';
import DriveGrid from '@picsart/cloud-storage/DriveGrid';
import { getContext } from '@picsart/miniapps-sdk';
import { FilePreviewFallback } from '@picsart/cloud-storage/FilePreview';

import { ANALYTICS_SERVICE_DATALAYER_NAME, ANALYTICS_SERVICE_ID, AnalyticsModulesPerSource, isEditorRoute, REMOTE_SETTINGS_TYPES } from 'config/constants';
import prepareAuthData from 'utils/prepareAuthData';
import useThemeChange from 'hooks/useThemeChange';
import { useUserContext } from 'context/user';
import Content from 'components/Content';
import useOpen from 'hooks/useOpen';

import useParams from 'hooks/useParams';

import './helpers/fetchInterceptor';
import { useRemoteSettings } from 'providers/RemoteSettingsProvider';
import { useEffect, useMemo } from 'react';
import { Loader, LoaderSizeXL, LoaderVariants } from '@picsart/design-system/components/Loader';
import GlobalLoader from 'components/GlobalLoader';
import useStyles from './useStyles';

const MY_FILES_DEFAULT_PARAMS: IFilesPageParams = {
  sortType: SortType.UPDATED,
  sortOrder: SortOrder.DESC,
  limit: '30',
  offset: '0',
  foldersLimit: '20',
};

const App = () => {
  const styles = useStyles();
  useThemeChange();
  const settings = useRemoteSettings();

  const isOpen = useOpen(() => {
    const source = (getContext().general.location.getState().params.shareSource as string) || 'create_flow';

    AnalyticsService.init(ANALYTICS_SERVICE_ID, AnalyticsModulesPerSource[source], ANALYTICS_SERVICE_DATALAYER_NAME, AnalyticsEnum.internal, 6000);
  });

  const user = useUserContext();

  const params = useParams();

  const remoteSettings = useMemo(
    () => ({
      drive_tools_touchpoint_config: settings[REMOTE_SETTINGS_TYPES.drive_tools_touchpoint_config],
      project_duplication_premium: settings.project_duplication_premium,
      commenting_enabled: settings.commenting_enabled,
      show_hidden_files: settings.show_hidden_files,
      drive_search_enabled: settings.drive_search_enabled,
    }),
    [settings],
  );

  const isPublicSharedRoute = params?.pathname?.includes('shared-public');
  const isSharedMode =
    params?.pathname?.includes('shared-public') || params?.pathname?.includes('shared-with-me') || getContext().general.sharedStorage.getState().drive?.initialFetchType === FetchOptions.Shared;

  const filesParams = useMemo(
    () => ({
      ...MY_FILES_DEFAULT_PARAMS,
      parentFolderUid: params.folder ? (params.folder as string) : '',
    }),
    [params.folder],
  );

  const filePreviewSkeleton = <FilePreviewFallback />;
  const driveSkeleton = (
    <DriveGrid>
      <DriveGrid.Header>
        <DriveHeaderFallback />
      </DriveGrid.Header>
      <DriveGrid.Folders>
        {Array.from({ length: 8 }).map((_, i) => (
          <RectangleFileItemFallback key={`folder-fallback-${i}`} />
        ))}
      </DriveGrid.Folders>
      <DriveGrid.Files>
        {Array.from({ length: 24 }).map((_, i) => (
          <SquareFileItemFallback key={`file-fallback-${i}`} />
        ))}
      </DriveGrid.Files>
    </DriveGrid>
  );

  useEffect(() => {
    getContext().handlers.onClose(() => {
      getContext().general.sharedStorage.dispatch({
        drive: {
          initialFetchType: FetchOptions.Files,
        },
      });
    });
  }, []);

  const externalNotifiers = {
    showNotification: getContext().layout.notification.show,
    showSnackbar: getContext().layout.snackbar.show,
    showDialog: getContext().layout.dialog.show,
  };

  const isEditor = getContext().general.location.getState().route.includes(isEditorRoute);

  if (!isOpen) {
    return <Loader variant={LoaderVariants.Global} isWithoutBackground size={LoaderSizeXL} />;
  }

  return user.isAuthorized || isPublicSharedRoute ? (
    <DriveProvider
      initialAuthState={prepareAuthData(user) as AuthProps}
      initialFetchType={isSharedMode ? FetchOptions.Shared : FetchOptions.Files}
      settings={remoteSettings}
      externalHeaders={getContext().utils.api.getHeaders()}
      filesParams={filesParams}
      externalNotifiers={externalNotifiers}
      isEditor={isEditor}
    >
      <PortalTracker />
      <Content />
      <GlobalLoader />
    </DriveProvider>
  ) : (
    <div className={styles.fallbackContainer}>{isPublicSharedRoute ? filePreviewSkeleton : driveSkeleton}</div>
  );
};

export default App;
